import type { AutoRefillResponse } from "@server/types/autoRefillTypes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import {
  autoRefillQueryKey,
  fetchAutoRefill,
  saveAutoRefill,
} from "@/api/auto-refill";
import { isBillingForbidden } from "@/api/billing";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/lib/query-keys";
import { ApiRequestError } from "@/utils/fetch";
import { AutoRefillCardForm } from "./AutoRefillCardForm";
import { formatCreditAmount as formatMinor } from "./billing-format";

type Data = AutoRefillResponse["data"];
function cents(text: string): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  const value = BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, "0"));
  return value > 0n && value <= 99_999_999n ? Number(value) : null;
}
function dollars(value: number) {
  return (value / 100).toFixed(2);
}
const reasonCopy: Record<string, string> = {
  retry_exhausted:
    "Payment status could not be confirmed. Contact support with your refill reference; do not start another payment.",
  payment_method_required:
    "Add or update your billing card in auto refill settings.",
  balance_still_low:
    "Your balance is still below the threshold. Increase the refill amount before enabling again.",
  not_eligible:
    "Auto refill requires an active prepaid account and a saved default card.",
  catalog_unavailable: "Auto refill pricing is temporarily unavailable.",
  provider_unavailable:
    "Billing is temporarily unavailable. Try again shortly.",
};
/** Organization-owned auto-refill controls; the enclosing admin shell unmounts this panel on org switch. */
export function AutoRefillPanel({ orgId }: { orgId: string }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: autoRefillQueryKey(orgId),
    queryFn: () => fetchAutoRefill(orgId),
    retry: false,
    staleTime: 1000,
    refetchInterval: (q) =>
      q.state.data?.active_attempt_id && !q.state.data.paused_reason
        ? 5000
        : false,
  });
  const disable = useMutation({
    mutationKey: ["billing", orgId, "auto-refill-save"],
    mutationFn: () => saveAutoRefill(orgId, { enabled: false }),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: ["billing", orgId, "auto-refill"],
      });
    },
  });
  if (isBillingForbidden(query.error) || isBillingForbidden(disable.error))
    return (
      <section aria-label="Auto refill">
        <p role="alert">Auto refill access denied.</p>
      </section>
    );
  if (query.isPending)
    return <section aria-label="Auto refill">Loading auto refill…</section>;
  if (!query.data)
    return (
      <section aria-label="Auto refill">
        <p>Auto refill is temporarily unavailable.</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          Retry auto refill
        </Button>
      </section>
    );
  const data = query.data;
  const needsCard = data.disabled_reason === "payment_method_required";
  if (!data.can_enable && !data.settings && !needsCard) return null;
  const paused = data.paused_reason !== null;
  return (
    <section
      aria-label="Auto refill"
      className="rounded-xl bg-card p-6 space-y-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium">Auto refill</h2>
          <p className="text-sm text-muted-foreground">
            Automatically add credits when your balance is low.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={(!data.can_enable && !needsCard) || disable.isPending}
            onClick={() => setOpen(true)}
          >
            {data.settings?.enabled ? "Edit auto refill" : "Enable auto refill"}
          </Button>
          <Button
            role="switch"
            aria-label="Auto refill enabled"
            aria-checked={data.settings?.enabled === true}
            disabled={
              disable.isPending ||
              (!data.settings?.enabled && !data.can_enable && !needsCard)
            }
            variant="outline"
            onClick={() =>
              data.settings?.enabled ? disable.mutate() : setOpen(true)
            }
          >
            {data.settings?.enabled ? "Turn off" : "Off"}
          </Button>
        </div>
      </div>
      {data.settings && (
        <p className="text-sm">
          Charge {formatMinor(data.settings.refill_amount_minor, "usd")} plus
          bonus credits when the available balance falls below{" "}
          {formatMinor(data.settings.minimum_balance_minor, "usd")}. Monthly
          card limit:{" "}
          {data.settings.maximum_monthly_charge_minor === null
            ? "No limit"
            : formatMinor(data.settings.maximum_monthly_charge_minor, "usd")}
          .
        </p>
      )}
      {data.active_attempt_id && !paused && (
        <p role="status">
          A refill is in progress. Your credits will appear after payment is
          confirmed.
        </p>
      )}
      {paused && (
        <p role="alert">
          Auto refill paused.{" "}
          {reasonCopy[data.paused_reason!] ??
            "This refill needs review. Contact support with your refill reference."}
        </p>
      )}
      {data.disabled_reason && (
        <p>
          {reasonCopy[data.disabled_reason] ?? "Auto refill is unavailable."}
        </p>
      )}
      {disable.isError && (
        <p role="alert">Could not turn off auto refill. Try again.</p>
      )}
      {open && (
        <AutoRefillDialog
          orgId={orgId}
          data={data}
          close={() => setOpen(false)}
          onSaved={async () => {
            await Promise.all([
              client.invalidateQueries({
                queryKey: ["billing", orgId, "auto-refill"],
              }),
              client.invalidateQueries({
                queryKey: queryKeys.billing.summary(orgId),
              }),
              client.invalidateQueries({
                queryKey: queryKeys.billing.purchases(orgId),
              }),
            ]);
            setOpen(false);
          }}
        />
      )}
    </section>
  );
}
function AutoRefillDialog({
  orgId,
  data,
  close,
  onSaved,
}: {
  orgId: string;
  data: Data;
  close(): void;
  onSaved(): Promise<void>;
}) {
  const id = useId();
  const [amount, setAmount] = useState(
    dollars(
      data.settings?.refill_amount_minor ?? data.presets_minor[0] ?? 10000,
    ),
  );
  const [customAmount, setCustomAmount] = useState(
    () =>
      data.presets_minor.length === 0 ||
      (data.settings !== null &&
        !data.presets_minor.includes(data.settings.refill_amount_minor)),
  );
  const [threshold, setThreshold] = useState(
    dollars(data.settings?.minimum_balance_minor ?? 500),
  );
  const [limit, setLimit] = useState(
    data.settings?.maximum_monthly_charge_minor
      ? dollars(data.settings.maximum_monthly_charge_minor)
      : "",
  );
  const [consent, setConsent] = useState(false);
  const [editingCard, setEditingCard] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  const charge = cents(amount),
    minimum = cents(threshold),
    cap = limit === "" ? null : cents(limit);
  const isCustomAmount =
    customAmount || !data.presets_minor.includes(charge ?? -1);
  const tier =
    charge === null
      ? null
      : [...(data.catalog?.tiers ?? [])]
          .reverse()
          .find((t) => charge >= t.from_amount_minor);
  const credit =
    charge && tier
      ? Number(
          (BigInt(charge) * BigInt(10000 + tier.bonus_bps) + 5000n) / 10000n,
        )
      : null;
  const valid =
    credit !== null &&
    credit <= 99_999_999 &&
    minimum !== null &&
    charge !== null &&
    charge > minimum &&
    charge >= 50 &&
    (limit === "" || (cap !== null && cap >= charge));
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["billing", orgId, "auto-refill-save"],
    mutationFn: async () => {
      if (
        !valid ||
        !consent ||
        !data.catalog ||
        !data.can_enable ||
        editingCard
      )
        throw new Error("invalid_settings");
      await saveAutoRefill(orgId, {
        enabled: true,
        refill_amount_minor: charge!,
        minimum_balance_minor: minimum!,
        maximum_monthly_charge_minor: cap,
        catalog_revision: data.catalog.revision,
      });
    },
    onSuccess: onSaved,
    onError: async (error) => {
      if (error instanceof ApiRequestError && error.status === 409)
        await client.invalidateQueries({
          queryKey: ["billing", orgId, "auto-refill"],
        });
    },
  });
  const conflict =
    mutation.error instanceof ApiRequestError && mutation.error.status === 409;
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !mutation.isPending && !cardBusy) close();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogTitle>Auto refill</DialogTitle>
        <DialogDescription>
          Use your saved billing card to refill your prepaid balance
          automatically.
        </DialogDescription>
        <section
          aria-label="Billing card"
          className="rounded-lg border p-4 space-y-3"
        >
          <h3 className="font-medium">Billing card</h3>
          {editingCard ? (
            <AutoRefillCardForm
              orgId={orgId}
              onBusyChange={setCardBusy}
              onCancel={() => setEditingCard(false)}
              onSaved={async () => {
                await client.invalidateQueries({
                  queryKey: autoRefillQueryKey(orgId),
                });
                setCardBusy(false);
                setEditingCard(false);
              }}
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {data.disabled_reason === "payment_method_required"
                  ? "Add a card to enable auto refill. Your card details are stored securely by Stripe."
                  : "Auto refill uses your saved billing card."}
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => setEditingCard(true)}
              >
                {data.disabled_reason === "payment_method_required"
                  ? "Add card"
                  : "Change card"}
              </Button>
            </>
          )}
        </section>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!mutation.isPending) mutation.mutate();
          }}
        >
          {data.presets_minor.length > 0 && (
            <fieldset>
              <legend className="mb-2 text-sm font-medium">
                Refill amount
              </legend>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {data.presets_minor.map((value) => {
                  const bonus =
                    [...(data.catalog?.tiers ?? [])]
                      .reverse()
                      .find((t) => value >= t.from_amount_minor)?.bonus_bps ??
                    0;
                  const cardCredits = Number(
                    (BigInt(value) * BigInt(10000 + bonus) + 5000n) / 10000n,
                  );
                  return (
                    <label key={value} className="cursor-pointer">
                      <input
                        type="radio"
                        name={`${id}-preset`}
                        className="peer sr-only"
                        checked={!isCustomAmount && charge === value}
                        onChange={() => {
                          setCustomAmount(false);
                          setAmount(dollars(value));
                        }}
                        aria-label={`${formatMinor(value, "usd")} card charge`}
                      />
                      <span className="flex h-full min-h-32 flex-col items-center justify-center gap-1 rounded-xl border-2 border-border px-2 py-4 text-center peer-checked:border-primary peer-checked:bg-primary/5 peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                        <span className="text-lg font-semibold">
                          {formatMinor(value, "usd")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatMinor(cardCredits, "usd")} in credits
                        </span>
                        {bonus > 0 && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            +{bonus / 100}% bonus credits
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name={`${id}-preset`}
                    className="peer sr-only"
                    checked={isCustomAmount}
                    onChange={() => setCustomAmount(true)}
                    aria-label="Other amount"
                  />
                  <span className="flex h-full min-h-32 items-center justify-center rounded-xl border-2 border-border p-4 font-medium peer-checked:border-primary peer-checked:bg-primary/5 peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                    Other
                  </span>
                </label>
              </div>
            </fieldset>
          )}
          {isCustomAmount && (
            <label className="block" htmlFor={`${id}-amount`}>
              Card charge (USD)
              <Input
                id={`${id}-amount`}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </label>
          )}
          <label className="block" htmlFor={`${id}-minimum`}>
            Minimum balance (USD)
            <Input
              id={`${id}-minimum`}
              inputMode="decimal"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              required
            />
          </label>
          <label className="block" htmlFor={`${id}-limit`}>
            Maximum monthly card spend (USD, optional)
            <Input
              id={`${id}-limit`}
              inputMode="decimal"
              placeholder="No limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </label>
          {charge !== null && credit !== null && (
            <p role="status">
              Add {formatMinor(credit, "usd")} in credits; charge{" "}
              {formatMinor(charge, "usd")} to your card (+
              {(tier?.bonus_bps ?? 0) / 100}% bonus credits).
            </p>
          )}
          {!valid && (
            <p>
              Enter a refill amount greater than the minimum balance. The card
              charge must be at least $0.50 and fit within the monthly limit.
            </p>
          )}
          <label className="flex gap-2 items-start">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            I authorize automatic charges to my saved card when my balance is
            low, under the current bonus tiers and selected monthly limit. I can
            turn auto refill off at any time.
          </label>
          {mutation.isError && (
            <p role="alert">
              {conflict
                ? "Pricing changed. Close and reopen this form to review the latest terms."
                : "Could not save auto refill. Check your billing card and try again."}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending || cardBusy}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !valid ||
                !consent ||
                !data.can_enable ||
                editingCard ||
                cardBusy ||
                mutation.isPending ||
                conflict
              }
            >
              {mutation.isPending ? "Saving…" : "Enable auto refill"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
